import { useState, useEffect, useCallback, useRef } from 'react'
import {
  FiTrendingUp, FiHome, FiClock, FiBriefcase,
  FiPhone, FiShoppingBag, FiPackage, FiCheckCircle,
  FiStar, FiMapPin,
  FiDroplet, FiFilter, FiZap, FiGift, FiCheck
} from 'react-icons/fi'
import { MapContainer, TileLayer, CircleMarker, Circle, useMap } from 'react-leaflet'
import { basemapUrl } from './lib/basemap'
import 'leaflet/dist/leaflet.css'

// ─── Paleta oficial Tupperware (promo 80 anos) — extraída de promocaotupperware.com.br ──
const B = {
  orange:   '#FF611F',   // primária — CTA principal
  orangeDk: '#E0551B',   // hover/escuro
  yellow:   '#F7C317',   // hero / destaque
  yellowHi: '#F7E401',   // amarelo vivo (botão "participe")
  pink:     '#FC5F95',   // CTA consumidor / acento
  blue:     '#4880C8',   // CTA "avançar / próxima etapa"
  purple:   '#C473B8',   // apoio
  pale:     '#FFF1EB',   // tom laranja suave (fundos)
  ink:      '#1f1a17',   // texto escuro
} as const

const FONT_BRAND   = "'Earl Tupper Sans', system-ui, -apple-system, sans-serif"
const FONT_DISPLAY = "'Roc Grotesk Compressed', 'Earl Tupper Sans', system-ui, sans-serif"
const FONT_BODY    = "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif"

const ANIM_CSS = `
  @keyframes stepIn    { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes iconBounce{ 0%,100%{transform:scale(1)} 50%{transform:scale(1.12)} }
  @keyframes ringPulse {
    0%   { box-shadow: 0 0 0 0   rgba(255,97,31,0.50) }
    100% { box-shadow: 0 0 0 12px rgba(255,97,31,0) }
  }
  @keyframes floaty  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
  .sc-input:focus { border-color: var(--sc-accent,#FF611F) !important; box-shadow:0 0 0 3px rgba(0,0,0,0.05); }
`

const PROGRESS_STEPS = 2

// Checkout de produtos (steps 4-5) oculto por enquanto — só "Chamar líder".
// Religar = trocar pra true.
const SHOW_CHECKOUT = false

const DEPOIMENTOS = [
  { quote: 'Comecei comprando com meu desconto e vendi pelo catálogo. Paguei o investimento em menos de 2 semanas e o restante ficou todo pra mim.', sigla: 'A.S.' },
  { quote: 'Eu compro pelo preço de revenda e vendo pelo preço da vitrine. A diferença fica comigo. É simples assim — e dá muito mais do que eu esperava.', sigla: 'C.M.' },
  { quote: 'Antes de comprar qualquer produto, já sei exatamente quanto vou lucrar. O catálogo já define o preço de venda. Sem risco de ficar no prejuízo.', sigla: 'R.O.' },
  { quote: 'Faço meus próprios horários, organizo meu dia do meu jeito e ainda ganho mais do que no emprego anterior. O desconto faz toda a diferença.', sigla: 'J.F.' },
]

const MOTIVACOES = [
  { id: 'renda_extra',       Icon: FiTrendingUp, img: '/uploads/renda_extra.gif',     label: 'Quero uma renda extra',        color: B.orange, bg: '#FFF1EB' },
  { id: 'sair_clt',          Icon: FiBriefcase,  img: '/uploads/tupperware_t_cl.gif',  label: 'Quero sair do emprego',        color: B.blue,   bg: '#EEF4FC' },
  { id: 'horario_flexivel',  Icon: FiClock,      img: '/uploads/tupperware_80.gif',    label: 'Quero escolher meus horários', color: B.pink,   bg: '#FFEEF5' },
  { id: 'trabalhar_de_casa', Icon: FiHome,       img: '/uploads/tupperware_t_casa.gif',label: 'Quero trabalhar de casa',      color: B.purple, bg: '#F8EFF6' },
]

// Imagens de produto por código (vitrine). Servidas de /uploads/produtos (bind-mount,
// live sem rebuild). Para adicionar: dropar o arquivo lá e mapear o código aqui.
// Aceita 1 imagem ou um array (galeria do mesmo produto) — o card usa a 1ª e o
// lightbox abre todas com navegação.
const PRODUCT_IMAGES: Record<number, string | string[]> = {
  883769: '/uploads/produtos/tijela_colors.jpeg', // Tigela Colors 500ml
  821272: [ // Descascador Estrela Algodão do Céu (galeria)
    '/uploads/produtos/descascador_estrela_algodao.jpeg',
    '/uploads/produtos/descascador_estrela_algodao_1.jpeg',
    '/uploads/produtos/descascador_estrela_algodao_2.jpeg',
  ],
  821322: [ // Funil Peônia (galeria)
    '/uploads/produtos/funil_peonia.jpeg',
    '/uploads/produtos/funil_peonia_2.jpeg',
  ],
  // Linha Jeitosinho — reusa a mesma foto em todas as variantes
  821415: '/uploads/produtos/jeitosinho_rech_carinho_feito_amor.jpeg', // Recheado com Carinho
  821483: '/uploads/produtos/jeitosinho_rech_carinho_feito_amor.jpeg', // 400ml Feito com Amor
  821482: '/uploads/produtos/jeitosinho_rech_carinho_feito_amor.jpeg', // 400ml Feito com Amor XG
  820532: '/uploads/produtos/jeitosinho_rech_carinho_feito_amor.jpeg', // 400ml Aqui tem Memória
  820533: '/uploads/produtos/jeitosinho_rech_carinho_feito_amor.jpeg', // 400ml Aqui tem Receita
  821370: '/uploads/produtos/jeitosinho_rech_carinho_feito_amor.jpeg', // 400ml Recheado Pack
  897218: [ // Super Chef Mini - Vermelho (galeria)
    '/uploads/produtos/super_chef_mini_1.jpeg',
    '/uploads/produtos/super_chef_mini_2.jpeg',
    '/uploads/produtos/super_chef_mini_3.jpeg',
    '/uploads/produtos/super_chef_mini_4.jpeg',
  ],
}
const imagesOf = (codigo: number): string[] => {
  const v = PRODUCT_IMAGES[codigo]; return v ? (Array.isArray(v) ? v : [v]) : []
}

// Combinação sugerida (curada manualmente) — soma ~R$399,86, bem próxima do mínimo.
// Por código de produto → quantidade.
const SUGGESTED_KIT: Record<number, number> = {
  883769: 4, // Tigela Colors 500ml
  821415: 6, // Jeitosinho Recheado com Carinho  ┐ idênticos (mesmo preço/foto):
  821483: 6, // Jeitosinho 400ml Feito com Amor  ┘ 6+6 = 12 Jeitosinho
  897218: 1, // Super Chef Mini - Vermelho
  821322: 1, // Funil Peônia
  821272: 1, // Descascador Estrela Algodão do Céu
}

function formatCep(v: string) { const d = v.replace(/\D/g,'').slice(0,8); return d.length>5?`${d.slice(0,5)}-${d.slice(5)}`:d }
function formatPhone(v: string) {
  const d = v.replace(/\D/g,'').slice(0,11)
  if (d.length<=2) return d
  if (d.length<=7) return `(${d.slice(0,2)}) ${d.slice(2)}`
  return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`
}
function prodStyle(nome: string): { Icon: React.ComponentType<{size?:number;color?:string}>, color: string, bg: string } {
  const n = nome.toLowerCase()
  if (n.includes('super') || n.includes('chef'))            return { Icon: FiStar,    color: B.pink,   bg: '#FFEEF5' }
  if (n.includes('jeitosinho') || n.includes('copo'))       return { Icon: FiDroplet, color: B.blue,   bg: '#EEF4FC' }
  if (n.includes('tigela') || n.includes('bowl') || n.includes('pote')) return { Icon: FiPackage, color: B.purple, bg: '#F8EFF6' }
  if (n.includes('descascador') || n.includes('cortador'))  return { Icon: FiZap,     color: B.orange, bg: '#FFF1EB' }
  if (n.includes('funil'))                                  return { Icon: FiFilter,  color: B.orange, bg: B.pale }
  return { Icon: FiPackage, color: '#9a8d84', bg: '#faf6f3' }
}
function fmt(v: number): string { return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }
function validPhone(v: string) { return v.replace(/\D/g,'').length >= 10 }
function firstName(nome: string) { return nome.trim().split(' ')[0] || '' }

interface NearbyMarker { lat: number; lng: number; initials: string; type: 'leader'|'consultora'; distance_km: number }
interface NearbyData   { center: { lat: number; lng: number; city: string|null }|null; markers: NearbyMarker[]; counts: { leaders: number; consultoras: number } }

function MapRecenter({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap()
  useEffect(() => { map.setView([lat, lng], 12) }, [lat, lng])
  return null
}

// ─── Botão-pílula estilo Tupperware (full-round, uppercase, fonte da marca) ──────
function Pill({ onClick, children, bg = B.orange, color = '#fff', disabled = false, className = '', style = {}, ariaLabel }: {
  onClick?: () => void; children: React.ReactNode; bg?: string; color?: string;
  disabled?: boolean; className?: string; style?: React.CSSProperties; ariaLabel?: string
}) {
  return (
    <button onClick={() => { if (!disabled) onClick?.() }} disabled={disabled} aria-label={ariaLabel}
      className={`inline-flex items-center justify-center text-center rounded-full uppercase leading-none transition-all active:scale-[0.96] disabled:opacity-40 disabled:active:scale-100 ${className}`}
      style={{ background: bg, color, border: '2px solid rgba(255,255,255,0.95)', fontFamily: FONT_BRAND, fontWeight: 900, letterSpacing: '0.02em', boxShadow: disabled ? 'none' : '0 6px 18px rgba(255,97,31,0.18)', ...style }}>
      {children}
    </button>
  )
}

// ─── Mini-timeline "como funciona" ──────────────────────────────────────────────
const TL_STEPS = [
  { n:'1', title:'Compre com desconto',   desc:'Você paga o preço de revenda — abaixo do catálogo.' },
  { n:'2', title:'Venda pelo catálogo',   desc:'O preço de venda já vem definido. Sem negociar.'       },
  { n:'3', title:'O lucro fica com você', desc:'A diferença entre os dois preços é toda sua.'          },
]
function StepsTimeline() {
  const [active, setActive] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setActive(a => (a + 1) % TL_STEPS.length), 1600)
    return () => clearInterval(t)
  }, [])
  return (
    <div className="flex flex-col">
      {TL_STEPS.map((s, i) => {
        const on = active === i
        return (
          <div key={s.n} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: on ? B.orange : B.pale, transform: on ? 'scale(1.15)' : 'scale(1)', transition: 'background 0.4s, transform 0.4s' }}>
                {on ? <FiCheck size={14} color="#fff" strokeWidth={3} /> : <span className="text-xs font-bold" style={{ color: B.orange }}>{s.n}</span>}
              </div>
              {i < TL_STEPS.length - 1 && (
                <div className="w-0.5 my-1" style={{ minHeight:22, background: active > i ? B.orange : B.pale, transition:'background 0.4s' }} />
              )}
            </div>
            <div className={i < TL_STEPS.length - 1 ? 'pb-3' : ''}>
              <p className="text-[13px] font-semibold leading-tight" style={{ color: on ? B.ink : '#b3a89f', transition:'color 0.4s' }}>{s.title}</p>
              <p className="text-[11px] leading-snug mt-0.5" style={{ color: on ? '#7a6f66' : '#cfc6bd', transition:'color 0.4s' }}>{s.desc}</p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function TestimonialSlider({ localLabel }: { localLabel: string|null }) {
  const [idx, setIdx] = useState(0)
  const timer = useRef<ReturnType<typeof setInterval>|null>(null)
  useEffect(() => { timer.current = setInterval(() => setIdx(i => (i+1)%DEPOIMENTOS.length), 4500); return () => { if (timer.current) clearInterval(timer.current) } }, [])
  const d = DEPOIMENTOS[idx]
  return (
    <div className="rounded-2xl px-3 py-3 select-none overflow-hidden" style={{ background:'#fff7f2', border:'1px solid rgba(255,97,31,0.14)' }}>
      <p className="italic text-[12px] leading-snug line-clamp-3" style={{ color:'#7a6f66' }}>"{d.quote}"</p>
      <footer className="mt-1.5 text-[11px] not-italic" style={{ color:'#b3a89f' }}>— {d.sigla}{localLabel ? ` · ${localLabel}` : ''}</footer>
      <div className="flex justify-center gap-1.5 mt-2">
        {DEPOIMENTOS.map((_,i) => (
          <span key={i} className="rounded-full transition-all" style={{ width: i===idx?16:6, height:6, background: i===idx?B.orange:'rgba(255,97,31,0.20)' }} />
        ))}
      </div>
    </div>
  )
}

// ─── Componente principal ────────────────────────────────────────────────────────
export default function SejaConsultora() {
  const params       = new URLSearchParams(window.location.search)
  const campaignSlug = params.get('campaign') || 'geral'
  const adId         = params.get('ad_id') || params.get('adid') || null
  const ctwaClid     = params.get('ctwa_clid') || null

  const [step,            setStep]            = useState(0)
  const [sessionId,       setSessionId]       = useState<string|null>(null)
  const [motivacao,       setMotivacao]       = useState<string|null>(null)
  const [nome,            setNome]            = useState('')
  const [cep,             setCep]             = useState('')
  const [endereco,        setEndereco]        = useState<string|null>(null)
  const [nearby,          setNearby]          = useState<NearbyData|null>(null)
  const [leaderWa,        setLeaderWa]        = useState<string|null>(null)
  const [whatsapp,        setWhatsapp]        = useState('')
  const [lgpd,            setLgpd]            = useState(false)
  const [loading,         setLoading]         = useState(false)
  const [cepLoading,      setCepLoading]      = useState(false)
  const [error,           setError]           = useState<string|null>(null)
  const [checkoutChoice,  setCheckoutChoice]  = useState<'kit'|'leader'|null>(null)
  const [products,        setProducts]        = useState<{codigo:number;nome:string;preco_consultora:number;preco_consumidor:number}[]>([])
  const [selectedKit,     setSelectedKit]     = useState<Record<number,number>>({})
  const [productsLoading, setProductsLoading] = useState(false)
  const [lightbox,        setLightbox]        = useState<{ images: string[]; index: number }|null>(null)
  const abandonSent = useRef(false)

  useEffect(() => {
    const el = document.createElement('style')
    el.id = 'seja-consultora-css'
    el.textContent = ANIM_CSS
    document.head.appendChild(el)
    return () => { document.getElementById('seja-consultora-css')?.remove() }
  }, [])

  useEffect(() => {
    fetch('/api/wizard/session', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ campaign_slug: campaignSlug, ad_id: adId, ctwa_clid: ctwaClid, referrer: document.referrer }),
    }).then(r=>r.json()).then(d=>{ if(d.session_id) setSessionId(d.session_id) }).catch(()=>{})
  }, [])

  // Abandono: registra se sair durante o funil (steps 1-2, antes de finalizar no 3)
  useEffect(() => {
    const onUnload = () => {
      if (sessionId && step>0 && step<3 && !abandonSent.current) {
        abandonSent.current = true
        navigator.sendBeacon('/api/wizard/abandon', JSON.stringify({ session_id: sessionId, step }))
      }
    }
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [sessionId, step])

  const recordStep = useCallback((s: number, answers?: object) => {
    if (!sessionId) return
    fetch('/api/wizard/step', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ session_id: sessionId, step: s, answers }) }).catch(()=>{})
  }, [sessionId])

  const goTo = useCallback((s: number, answers?: object) => {
    if (s>0) recordStep(s, answers)
    setError(null); setStep(s); window.scrollTo(0,0)
  }, [recordStep])

  // CEP → endereço + líderes/consultoras próximas (preview inline no cadastro)
  useEffect(() => {
    const digits = cep.replace(/\D/g,'')
    if (digits.length!==8) { setEndereco(null); setNearby(null); return }
    setCepLoading(true)
    Promise.all([
      fetch('/api/wizard/cep', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ session_id: sessionId, cep: digits }) }).then(r=>r.json()),
      fetch(`/api/wizard/nearby/${digits}`).then(r=>r.json()),
    ]).then(([cepData, nearbyData]) => { setEndereco(cepData.endereco||null); setNearby(nearbyData) })
      .catch(()=>{}).finally(()=>{ setCepLoading(false) })
  }, [cep, sessionId])

  // Produtos (passo opcional de kit, após confirmação)
  useEffect(() => {
    if (step!==4 || products.length>0 || productsLoading) return
    setProductsLoading(true)
    fetch('/api/wizard/products').then(r=>r.json()).then(d=>setProducts(d.products||[])).catch(()=>{}).finally(()=>setProductsLoading(false))
  }, [step])

  const handleComplete = async () => {
    if (nome.trim().length<2 || !validPhone(whatsapp) || !lgpd) return
    setLoading(true); setError(null)
    try {
      const r = await fetch('/api/wizard/complete', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ session_id: sessionId, nome: nome.trim(), whatsapp: whatsapp.replace(/\D/g,''), cep: cep.replace(/\D/g,''), cidade: endereco, motivacao, answers: { motivacao, cep, nome: nome.trim() } }),
      })
      const d = await r.json()
      if (d.ok) { if (d.leader_whatsapp) setLeaderWa(d.leader_whatsapp); goTo(3) }
      else setError('Não conseguimos salvar. Tente novamente.')
    } catch { setError('Erro de conexão. Tente novamente.') }
    finally { setLoading(false) }
  }

  const enderecoParts = endereco ? endereco.split(',').map(s=>s.trim()) : []
  const bairroLabel   = enderecoParts.length>1 ? enderecoParts[0] : null
  const cidadeLabel   = enderecoParts.length>0 ? enderecoParts[enderecoParts.length-1] : null
  const localLabel    = bairroLabel || cidadeLabel

  const fn           = firstName(nome)
  const nConsultoras = nearby?.counts.consultoras ?? 0
  const nLeaders     = nearby?.counts.leaders ?? 0
  const totalProximas = nConsultoras + nLeaders
  const cadastroValido = nome.trim().length>=2 && validPhone(whatsapp) && cep.replace(/\D/g,'').length>=8 && lgpd

  // Vitrine curada: top 6 com o mínimo de imagens repetidas. Mantém 1 produto por
  // imagem distinta (Jeitosinho domina o top de vendas e compartilham a mesma foto);
  // se sobrar espaço pra chegar a 6, completa com as repetições restantes.
  const curatedProducts = (() => {
    const seen = new Set<string>(); const primary: typeof products = []; const extra: typeof products = []
    for (const p of products) {
      const key = imagesOf(p.codigo)[0] || `icon-${p.codigo}`
      if (seen.has(key)) extra.push(p)
      else { seen.add(key); primary.push(p) }
    }
    return [...primary, ...extra].slice(0, 6)
  })()

  const kitTotal = Object.entries(selectedKit).reduce((sum,[cod,qty]) => {
    const p = curatedProducts.find(p=>p.codigo===Number(cod)); return sum+(p?p.preco_consultora*qty:0)
  }, 0)
  const kitCount = Object.values(selectedKit).reduce((s,q)=>s+q,0)
  // "Sugerir combinação": aplica a combinação curada (SUGGESTED_KIT), só pros
  // produtos disponíveis na vitrine no momento.
  const autoMontarKit = () => {
    const available = new Set(curatedProducts.map(p => p.codigo))
    const kit: Record<number,number> = {}
    for (const [cod, qty] of Object.entries(SUGGESTED_KIT)) {
      if (available.has(Number(cod))) kit[Number(cod)] = qty
    }
    setSelectedKit(kit)
  }

  const progressPct = step>=1&&step<=2 ? Math.round((step/PROGRESS_STEPS)*100) : 0

  // Nunca gerar scroll horizontal (decoração negativa)
  useEffect(() => {
    const html = document.documentElement, body = document.body
    const ph = html.style.overflowX, pb = body.style.overflowX
    html.style.overflowX = 'hidden'; body.style.overflowX = 'hidden'
    return () => { html.style.overflowX = ph; body.style.overflowX = pb }
  }, [])

  // Lightbox: Esc fecha, ←/→ navegam a galeria
  useEffect(() => {
    if (!lightbox) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null)
      else if (e.key === 'ArrowRight') setLightbox(lb => lb && ({ ...lb, index: (lb.index+1)%lb.images.length }))
      else if (e.key === 'ArrowLeft')  setLightbox(lb => lb && ({ ...lb, index: (lb.index-1+lb.images.length)%lb.images.length }))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox])

  // ─── Paleta por etapa ────────────────────────────────────────────────────────
  // Cada etapa tem sua própria paleta: fundo (bloco de cor) e accent
  // (label/título/CTA/progresso). O card permanece branco pra legibilidade.
  const STEP_THEMES: Record<number, { from:string; to:string; accent:string; soft:string }> = {
    0: { from:B.orange, to:B.yellow,  accent:B.orange, soft:'#FFF1EB' }, // laranja/amarelo
    1: { from:B.pink,   to:'#FF8FB8', accent:B.pink,   soft:'#FFEEF5' }, // rosa
    2: { from:B.blue,   to:'#7FB2EA', accent:B.blue,   soft:'#EEF4FC' }, // azul
    3: { from:B.purple, to:'#E59AD2', accent:B.purple, soft:'#F6ECF4' }, // roxo/rosa
    4: { from:B.yellow, to:B.orange,  accent:B.orange, soft:'#FFF1EB' }, // amarelo/laranja
    5: { from:B.pink,   to:B.purple,  accent:B.pink,   soft:'#FFEEF5' }, // rosa/roxo
  }
  const theme = STEP_THEMES[step] || STEP_THEMES[0]
  const A = theme.accent, Asoft = theme.soft

  // ─── Estilos base ──────────────────────────────────────────────────────────
  // Layout híbrido: fundo em bloco de cor da marca (varia por etapa) + card claro.
  const bgStyle: React.CSSProperties = {
    background: `linear-gradient(165deg, ${theme.from} 0%, ${theme.from} 38%, ${theme.to} 100%)`,
    minHeight: '100dvh',
    transition: 'background 0.5s ease',
  }
  const cardStyle: React.CSSProperties = {
    background: '#ffffff',
    boxShadow: '0 18px 50px rgba(120,40,0,0.28)',
    border: '1px solid rgba(255,255,255,0.9)',
  }
  const inputStyle: React.CSSProperties = { background:'#fff', border:'1.5px solid #ead9cf', color:B.ink }
  const rowStyle:   React.CSSProperties = { background:'#fff7f2', border:'1px solid rgba(255,97,31,0.12)' }
  const labelCls = 'text-[10px] font-bold uppercase tracking-[0.14em]'
  const fieldLabelCls = `${labelCls} block mb-1.5`
  const inputCls = 'sc-input w-full rounded-xl px-4 py-3.5 text-[16px] outline-none'

  return (
    <div className="relative flex flex-col items-center justify-center"
      style={{ ...bgStyle, padding:'18px', fontFamily: FONT_BODY, '--sc-accent': A } as React.CSSProperties}>
      <style>{ANIM_CSS}</style>

      {/* Decoração fixa ao viewport — textura de marca sutil, clipada, sem afetar o scroll */}
      <div aria-hidden className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Camada quadrada (200vmax) centrada e girada: a textura é repeat, então
            estendê-la muito além do viewport evita os cantos vazios que a rotação expõe. */}
        <div className="absolute left-1/2 top-1/2 w-[200vmax] h-[200vmax]"
          style={{ transform:'translate(-50%,-50%) rotate(-30deg)', backgroundImage:'url(/uploads/tupperware-watermark.png)', backgroundSize:'320px 240px', backgroundRepeat:'repeat', opacity:0.05, filter:'brightness(0)' }} />
      </div>

      {/* Card claro */}
      <div className="relative z-10 w-full max-w-md rounded-[28px] flex flex-col overflow-hidden"
        style={{ ...cardStyle }}>

        {/* Faixa de marca no topo do card — logo width-driven (ratio 2.81), responsivo e sem estourar */}
        <div className="flex-shrink-0 flex items-center justify-center pt-6 pb-3 px-2">
          <img src="/logo_animada_preta.gif" alt="Tupperware" className="w-[86%] max-w-[320px] h-auto" />
        </div>

        {/* Conteúdo das etapas */}
        <div key={step} className="px-6 pb-5" style={{ animation:'stepIn 0.28s ease-out both' }}>

          {/* ───────────── 0: Hero / Gancho ───────────── */}
          {step===0 && (
            <div className="flex flex-col gap-5">
              <div>
                <p className={labelCls} style={{ color:A }}>Tupperware · Distribuição CG-MS</p>
                <h1 className="mt-2 uppercase" style={{ fontFamily:FONT_DISPLAY, color:A, fontSize:34, lineHeight:0.96, letterSpacing:'0.01em' }}>
                  Ganhe uma renda extra com a Tupperware
                </h1>
                <p className="mt-2.5 text-[13.5px] leading-relaxed" style={{ color:'#7a6f66' }}>
                  Responda 2 perguntas rápidas e veja como começar a vender — do seu jeito.
                </p>
              </div>
              <div className="rounded-2xl px-4 py-3.5" style={rowStyle}><StepsTimeline /></div>
              <Pill onClick={() => goTo(1)} bg={A} className="w-full py-4 text-[16px]">
                Quero participar →
              </Pill>
              <p className="text-center text-[11px]" style={{ color:'#b3a89f' }}>Leva menos de 1 minuto · sem compromisso</p>
            </div>
          )}

          {/* ───────────── 1: Motivação ───────────── */}
          {step===1 && (
            <div className="flex flex-col gap-3.5">
              <div>
                <p className={labelCls} style={{ color:A }}>Pergunta 1 de 2</p>
                <h2 className="mt-1.5 uppercase" style={{ fontFamily:FONT_DISPLAY, color:B.ink, fontSize:26, lineHeight:0.98 }}>O que você está buscando?</h2>
                <p className="mt-1 text-[12px]" style={{ color:'#9a8d84' }}>Escolha a opção que mais combina com você.</p>
              </div>
              <div className="flex flex-col gap-2.5">
                {MOTIVACOES.map(m => {
                  const active = motivacao===m.id
                  return (
                    <button key={m.id}
                      onClick={() => { setMotivacao(m.id); setTimeout(() => goTo(2,{motivacao:m.id}), 240) }}
                      className="w-full flex items-center gap-3 rounded-2xl px-3.5 py-2.5 text-left transition-all active:scale-[0.99]"
                      style={{ background: active?m.bg:'#fbf8f6', border: active?`2px solid ${m.color}`:'2px solid #f0e8e2' }}>
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: m.img ? 'transparent' : m.bg }}>
                        {m.img ? <img src={m.img} alt="" style={{width:42,height:42,objectFit:'contain'}} /> : <m.Icon size={20} color={m.color} />}
                      </div>
                      <span className="text-[14px] font-semibold flex-1" style={{ color:B.ink, fontFamily:FONT_BRAND }}>{m.label}</span>
                      {active && <FiCheckCircle size={18} color={m.color} />}
                    </button>
                  )
                })}
              </div>
              <button onClick={() => goTo(0)} className="text-[11px] text-center" style={{ color:'#b3a89f' }}>← Voltar</button>
            </div>
          )}

          {/* ───────────── 2: Cadastro UNIFICADO (nome + WhatsApp + CEP + LGPD) ───────────── */}
          {step===2 && (
            <div className="flex flex-col gap-4">
              <div>
                <p className={labelCls} style={{ color:A }}>Pergunta 2 de 2</p>
                <h2 className="mt-1.5 uppercase" style={{ fontFamily:FONT_DISPLAY, color:B.ink, fontSize:26, lineHeight:0.98 }}>Quase lá! Seus dados</h2>
                <p className="mt-1 text-[12px]" style={{ color:'#9a8d84' }}>Sua líder vai te chamar no WhatsApp pra dar as boas-vindas.</p>
              </div>

              <div>
                <label className={fieldLabelCls} style={{ color: A }}>Como podemos te chamar?</label>
                <input type="text" placeholder="Seu nome" value={nome} autoFocus
                  onChange={e => setNome(e.target.value)} className={inputCls} style={inputStyle} />
              </div>

              <div>
                <label className={fieldLabelCls} style={{ color: A }}>Seu WhatsApp</label>
                <input type="text" inputMode="tel" placeholder="(67) 99999-9999" value={whatsapp}
                  onChange={e => setWhatsapp(formatPhone(e.target.value))} className={inputCls} style={inputStyle} />
              </div>

              <div>
                <label className={fieldLabelCls} style={{ color: A }}>Seu CEP</label>
                <input type="text" inputMode="numeric" placeholder="00000-000" value={cep}
                  onChange={e => setCep(formatCep(e.target.value))} className={inputCls} style={inputStyle} />
                {cepLoading && <p className="mt-2 text-xs animate-pulse" style={{ color:'#b3a89f' }}>Buscando endereço…</p>}
                {endereco && !cepLoading && (
                  <div className="mt-2 rounded-xl px-3 py-2 flex items-start gap-2" style={rowStyle}>
                    <FiMapPin size={14} color={A} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[12px] font-medium" style={{ color:B.ink }}>{endereco}</p>
                      {totalProximas>0 && (
                        <p className="text-[11px] mt-0.5" style={{ color:'#7a6f66' }}>
                          {nLeaders>0 ? `Sua líder está por perto` : ''}{nLeaders>0&&nConsultoras>0?' · ':''}
                          {nConsultoras>0 ? `${nConsultoras} revendas ativas na região` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={lgpd} onChange={e => setLgpd(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded flex-shrink-0" style={{ accentColor:A }} />
                <span className="text-[11px] leading-relaxed" style={{ color:'#9a8d84' }}>
                  Concordo que a TWMS use meu WhatsApp para entrar em contato sobre esta oportunidade. Posso cancelar quando quiser.
                </span>
              </label>

              {error && <p className="text-xs" style={{ color:'#dc2626' }}>{error}</p>}

              <Pill onClick={handleComplete} disabled={!cadastroValido||loading} bg={A} className="w-full py-4 text-[16px]">
                {loading ? 'Enviando…' : 'Quero começar →'}
              </Pill>
              <button onClick={() => goTo(1)} className="text-[11px] text-center" style={{ color:'#b3a89f' }}>← Voltar</button>
            </div>
          )}

          {/* ───────────── 3: Sucesso + próximos passos ───────────── */}
          {step===3 && checkoutChoice!=='leader' && (
            <div className="flex flex-col gap-3.5">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-2" style={{ background:Asoft, animation:'iconBounce 2s ease-in-out infinite' }}>
                  <FiCheckCircle size={26} color={A} />
                </div>
                <h2 className="uppercase" style={{ fontFamily:FONT_DISPLAY, color:A, fontSize:28, lineHeight:0.98 }}>{fn?`Pronto, ${fn}!`:'Cadastro confirmado!'}</h2>
                <p className="mt-1.5 text-[13px]" style={{ color:'#7a6f66' }}>Já avisamos sua líder — ela vai te chamar pelo WhatsApp em breve.</p>
              </div>

              {/* Dois blocos: próximos passos | território da líder */}
              <div className="grid grid-cols-2 gap-2.5 items-stretch">
                {/* Bloco 1 — próximos passos */}
                <div className="rounded-2xl px-3 py-3 flex flex-col" style={rowStyle}>
                  <p className={labelCls} style={{ color:A, marginBottom:8 }}>Próximos passos</p>
                  <div className="flex flex-col gap-2.5">
                    {[
                      'Sua líder te chama no WhatsApp.',
                      'Ela explica o desconto e o catálogo.',
                      'Você compra com desconto e revende.',
                    ].map((t,i) => (
                      <div key={i} className="flex items-start gap-1.5">
                        <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background:A }}>
                          <span className="text-[9px] font-bold text-white" style={{ fontFamily:FONT_BRAND }}>{i+1}</span>
                        </div>
                        <span className="text-[11px] leading-tight" style={{ color:'#5e554d' }}>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bloco 2 — território / cobertura da líder */}
                <div className="rounded-2xl overflow-hidden flex flex-col" style={rowStyle}>
                  <div className="relative" style={{ height:104 }}>
                    {nearby?.center ? (
                      <MapContainer center={[nearby.center.lat, nearby.center.lng]} zoom={12}
                        style={{ height:'100%', width:'100%' }} zoomControl={false} scrollWheelZoom={false}
                        attributionControl={false} dragging={false} doubleClickZoom={false}>
                        <MapRecenter lat={nearby.center.lat} lng={nearby.center.lng} />
                        <TileLayer url={basemapUrl(false)} />
                        <Circle center={[nearby.center.lat, nearby.center.lng]} radius={5000}
                          pathOptions={{ color:A, fillColor:A, fillOpacity:0.10, weight:1.5, dashArray:'5 4' }} />
                        {nearby.markers.filter(m=>m.type==='leader').map((m,i)=>(
                          <CircleMarker key={`l-${i}`} center={[m.lat,m.lng]} radius={6} pathOptions={{ color:'#fff', fillColor:A, fillOpacity:0.9, weight:1.5 }} />
                        ))}
                        <CircleMarker center={[nearby.center.lat, nearby.center.lng]} radius={8} pathOptions={{ color:'#fff', fillColor:B.orange, fillOpacity:1, weight:2 }} />
                      </MapContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center" style={{ background:Asoft }}>
                        <FiMapPin size={26} color={A} />
                      </div>
                    )}
                    <span className="absolute bottom-1.5 left-1.5 z-[500] flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold text-white" style={{ background:A }}>
                      <FiMapPin size={9} /> raio 5 km
                    </span>
                    {/* ODbL: o attributionControl do Leaflet está desligado porque
                        não cabe num cartão de 104px — mas o crédito ao OSM é
                        obrigatório e esta página recebe tráfego pago. Vai aqui. */}
                    <span className="absolute bottom-1 right-1.5 z-[500] text-[8px] leading-none" style={{ color:'#8a807a' }}>
                      © OpenMapTiles © OpenStreetMap
                    </span>
                  </div>
                  <div className="px-3 py-2.5 flex-1">
                    <p className="text-[10.5px] leading-snug" style={{ color:'#5e554d' }}>
                      Comece com <b style={{color:A}}>amigos, família e vizinhos</b>. O raio de 5 km é a área de cobertura da sua líder.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTAs: "Aproveitar" (checkout) oculto enquanto SHOW_CHECKOUT=false */}
              <div className={`grid ${SHOW_CHECKOUT ? 'grid-cols-2' : 'grid-cols-1'} gap-2.5`}>
                <Pill bg={A} className="py-3 text-[12px]"
                  onClick={() => {
                    const msg = encodeURIComponent(`Olá! Acabei de me cadastrar pelo site para revender Tupperware. Meu nome é ${nome.trim()}. 😊`)
                    window.open(leaderWa ? `https://wa.me/${leaderWa}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank')
                  }}>
                  <FiPhone size={15} className="mr-1.5" /> Chamar líder
                </Pill>
                {SHOW_CHECKOUT && (
                <button onClick={() => {
                  setCheckoutChoice('kit')
                  if (products.length===0&&!productsLoading) {
                    setProductsLoading(true)
                    fetch('/api/wizard/products').then(r=>r.json()).then(d=>setProducts(d.products||[])).catch(()=>{}).finally(()=>setProductsLoading(false))
                  }
                  setStep(4)
                }} className="rounded-full py-3 text-[12px] uppercase inline-flex items-center justify-center leading-none transition-all active:scale-[0.96]"
                  style={{ border:`2px solid ${A}`, color:A, background:'#fff', fontFamily:FONT_BRAND, fontWeight:900, letterSpacing:'0.02em' }}>
                  <FiShoppingBag size={15} className="mr-1.5" /> Aproveitar
                </button>
                )}
              </div>

              <TestimonialSlider localLabel={localLabel} />
            </div>
          )}

          {/* ───────────── 4: Produtos (kit, opcional) ───────────── */}
          {step===4 && (
            <div className="flex flex-col gap-3">
              <div>
                <p className={labelCls} style={{ color:A }}>Preço de revenda</p>
                <h2 className="mt-1 uppercase" style={{ fontFamily:FONT_DISPLAY, color:B.ink, fontSize:23, lineHeight:0.98 }}>Escolha o que comprar com desconto</h2>
                <p className="mt-1 text-[11px]" style={{ color:'#9a8d84' }}>Você compra por este preço. Vende pelo catálogo. A diferença é sua.</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs" style={{ color:'#9a8d84' }}>{kitCount} item{kitCount!==1?'s':''}</span>
                  <span className="text-sm font-bold" style={{ color: kitTotal>=399?B.orange:B.ink, fontFamily:FONT_BRAND }}>{fmt(kitTotal)}</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background:'#f0e8e2' }}>
                  <div className="h-full rounded-full transition-all duration-300" style={{ width:`${Math.min((kitTotal/399)*100,100)}%`, background: kitTotal>=399?B.orange:'#ffc9ad' }} />
                </div>
                {kitTotal>0&&kitTotal<399 && <p className="mt-1 text-[10px]" style={{ color:'#b3a89f' }}>Faltam {fmt(399-kitTotal)} para o mínimo.</p>}
              </div>

              <button onClick={autoMontarKit} className="w-full rounded-xl py-2.5 text-xs font-bold uppercase transition-all active:scale-[0.98] flex items-center justify-center gap-1.5"
                style={{ background:B.pale, color:B.orange, border:'1.5px solid rgba(255,97,31,0.25)', fontFamily:FONT_BRAND }}>
                <FiStar size={13} /> Sugerir combinação (~R$399)
              </button>

              {productsLoading ? (
                <div className="text-center py-6 text-sm animate-pulse" style={{ color:'#b3a89f' }}>Carregando produtos…</div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {curatedProducts.map(p => {
                    const qty = selectedKit[p.codigo]||0
                    const selected = qty>0
                    const setQty = (n: number) => setSelectedKit(prev=>({...prev,[p.codigo]:Math.max(0,Math.min(n,99))}))
                    const ps = prodStyle(p.nome)
                    const imgs = imagesOf(p.codigo)
                    const img = imgs[0]
                    return (
                      <div key={p.codigo} className="rounded-2xl overflow-hidden flex flex-col transition-all"
                        style={{ background:'#fff', border: selected?`2px solid ${A}`:'1.5px solid #f0e8e2', boxShadow: selected?`0 4px 14px ${A}22`:'0 1px 4px rgba(0,0,0,0.04)' }}>
                        {/* Imagem (vitrine): zoom suave no hover + clique abre o lightbox/galeria */}
                        <div className="group relative aspect-square w-full overflow-hidden" style={{ background: img?'#fff':ps.bg }}>
                          {img
                            ? <img src={img} alt={p.nome} loading="lazy"
                                onClick={() => setLightbox({ images: imgs, index: 0 })}
                                className="w-full h-full object-cover cursor-zoom-in transition-transform duration-500 ease-out group-hover:scale-110" />
                            : <div className="w-full h-full flex items-center justify-center"><ps.Icon size={34} color={ps.color} /></div>}
                          {imgs.length>1 && (
                            <span className="absolute top-1.5 left-1.5 z-10 rounded-full px-1.5 py-0.5 text-[8.5px] font-bold text-white pointer-events-none" style={{ background:'rgba(0,0,0,0.45)' }}>
                              1/{imgs.length}
                            </span>
                          )}
                          <div className="absolute bottom-1.5 right-1.5 z-10">
                            {qty===0 ? (
                              <button onClick={() => setQty(1)} aria-label="Adicionar"
                                className="w-7 h-7 rounded-full flex items-center justify-center text-[18px] leading-none font-bold shadow-md active:scale-90 transition-transform"
                                style={{ background:A, color:'#fff', border:'1.5px solid #fff' }}>+</button>
                            ) : (
                              <div className="flex items-center gap-0.5 rounded-full shadow-md px-0.5 py-0.5" style={{ background:'#fff', border:`1.5px solid ${A}` }}>
                                <button onClick={() => setQty(qty-1)} aria-label="Tirar"
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-[15px] leading-none font-bold active:scale-90 transition-transform" style={{ color:A }}>−</button>
                                <span className="text-[11px] font-bold text-center" style={{ color:B.ink, minWidth:14, fontFamily:FONT_BRAND }}>{qty}</span>
                                <button onClick={() => setQty(qty+1)} aria-label="Adicionar"
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-[14px] leading-none font-bold active:scale-90 transition-transform" style={{ background:A, color:'#fff' }}>+</button>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Título + preço FORA da imagem */}
                        <div className="px-2 py-1.5">
                          <p className="text-[10.5px] font-semibold leading-tight line-clamp-2" style={{ color:B.ink, minHeight:'2.4em' }}>{p.nome}</p>
                          <div className="mt-0.5 flex items-baseline gap-1.5">
                            <span className="text-[12.5px] font-bold" style={{ color:A, fontFamily:FONT_BRAND }}>{fmt(p.preco_consultora)}</span>
                            {p.preco_consumidor>p.preco_consultora && <span className="text-[9px] line-through" style={{ color:'#cbbfb5' }}>{fmt(p.preco_consumidor)}</span>}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              <Pill disabled={kitTotal<399} bg={A} className="w-full py-3.5 text-[14px]"
                onClick={() => {
                  fetch('/api/wizard/step', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ session_id: sessionId, step:4, answers:{ kit: selectedKit, kit_total: kitTotal } }) }).catch(()=>{})
                  setCheckoutChoice('kit'); setStep(5)
                }}>
                {kitTotal>=399?`Confirmar — ${fmt(kitTotal)} →`:`Faltam ${fmt(399-kitTotal)}`}
              </Pill>
              <button onClick={() => { setStep(3); setCheckoutChoice(null) }} className="text-[11px] text-center" style={{ color:'#b3a89f' }}>← Voltar</button>
            </div>
          )}

          {/* ───────────── 5: Pedido confirmado ───────────── */}
          {step===5 && (
            <div className="flex flex-col gap-4 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ background:Asoft, animation:'iconBounce 2.5s ease-in-out infinite' }}>
                <FiGift size={26} color={A} />
              </div>
              <div>
                <h2 className="uppercase" style={{ fontFamily:FONT_DISPLAY, color:A, fontSize:28, lineHeight:0.98 }}>{fn?`Pedido reservado, ${fn}!`:'Pedido reservado!'}</h2>
                <p className="mt-1.5 text-[13px]" style={{ color:'#7a6f66' }}>Sua líder{localLabel?` na ${localLabel}`:''} vai entrar em contato pra confirmar e combinar a entrega.</p>
              </div>
              <div className="rounded-2xl px-4 py-3 text-left" style={rowStyle}>
                <p className="text-[11px] font-bold mb-2 uppercase" style={{ color:A, fontFamily:FONT_BRAND }}>Seu kit · {fmt(kitTotal)}</p>
                {curatedProducts.filter(p=>(selectedKit[p.codigo]||0)>0).map(p => (
                  <div key={p.codigo} className="flex justify-between items-center py-1.5 text-xs border-b last:border-0" style={{ borderColor:'rgba(255,97,31,0.1)' }}>
                    <span className="truncate" style={{ color:'#5e554d' }}>{p.nome.length>22?p.nome.slice(0,22)+'…':p.nome}</span>
                    <span className="font-semibold ml-2 flex-shrink-0" style={{ color:B.ink, fontFamily:FONT_BRAND }}>
                      {selectedKit[p.codigo]>1?`×${selectedKit[p.codigo]} `:''}{fmt(p.preco_consultora*(selectedKit[p.codigo]||0))}
                    </span>
                  </div>
                ))}
                <p className="text-[10px] mt-2" style={{ color:'#b3a89f' }}>Checkout com pagamento via Asaas em breve.</p>
              </div>
            </div>
          )}

        </div>{/* /conteúdo */}

        {/* Rodapé: progresso + selo legal */}
        <div className="flex-shrink-0 px-6 pt-1 pb-4">
          {step>=1 && step<=2 && (
            <div className="mb-2">
              <div className="flex justify-between items-center mb-1">
                <span className="text-[10px]" style={{ color:'#b3a89f' }}>passo {step} de {PROGRESS_STEPS}</span>
                <span className="text-[10px]" style={{ color:'#b3a89f' }}>{progressPct}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background:'#f0e8e2' }}>
                <div style={{ width:`${progressPct}%`, height:'100%', borderRadius:'1rem', background:`linear-gradient(90deg, ${theme.from}, ${theme.to})`, transition:'width 0.5s ease' }} />
              </div>
            </div>
          )}
          <p className="text-[8px] text-justify uppercase tracking-wide leading-relaxed" style={{ color:'rgba(120,40,0,0.30)' }}>
            AVANÇO ARTIGOS DOMÉSTICOS LTDA · DISTRIBUIDORA AUTORIZADA TUPPERWARE · R. MARECHAL RONDON, 142 · AMAMBAI · CAMPO GRANDE/MS · CEP 79.008-001 · CNPJ 00.643.504/0001-20 · POLÍTICA DE PRIVACIDADE · LGPD
          </p>
        </div>
      </div>{/* /card */}

      {/* Lightbox da vitrine — pronto pra galeria (setas/dots quando há +de 1 imagem) */}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background:'rgba(0,0,0,0.86)', backdropFilter:'blur(4px)' }}
          onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} aria-label="Fechar"
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center text-white text-2xl leading-none active:scale-90 transition-transform"
            style={{ background:'rgba(255,255,255,0.16)' }}>×</button>

          <img key={lightbox.index} src={lightbox.images[lightbox.index]} alt="" onClick={e => e.stopPropagation()}
            className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl" style={{ animation:'stepIn 0.22s ease-out both' }} />

          {lightbox.images.length>1 && (
            <>
              <button aria-label="Anterior"
                onClick={e => { e.stopPropagation(); setLightbox(lb => lb && ({ ...lb, index:(lb.index-1+lb.images.length)%lb.images.length })) }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center text-white text-3xl leading-none active:scale-90 transition-transform"
                style={{ background:'rgba(255,255,255,0.16)' }}>‹</button>
              <button aria-label="Próxima"
                onClick={e => { e.stopPropagation(); setLightbox(lb => lb && ({ ...lb, index:(lb.index+1)%lb.images.length })) }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center text-white text-3xl leading-none active:scale-90 transition-transform"
                style={{ background:'rgba(255,255,255,0.16)' }}>›</button>
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-1.5">
                {lightbox.images.map((_, i) => (
                  <button key={i} aria-label={`Imagem ${i+1}`}
                    onClick={e => { e.stopPropagation(); setLightbox(lb => lb && ({ ...lb, index:i })) }}
                    className="rounded-full transition-all" style={{ width: i===lightbox.index?20:7, height:7, background: i===lightbox.index?'#fff':'rgba(255,255,255,0.45)' }} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
